import config from '../../configLoader';
import { PayrollPunchPayloadKind, SiteConfig } from '../../interfaces/ConfigInterface';
import { HourlyProject, TimeEntryHourly } from '../../interfaces/EmployeeInterface';

/**
 * Payroll editor — the 2020 hourly punch shapes (pure, no React).
 *
 * 2020PH/NV clock piece work into TimeEntryHourly too, so one TEH table carries
 * four shapes the mobile app names by PunchPayloadKind:
 *
 *   Piece hours      project, no phase, no account, no SO        HourlyJob
 *   Hourly · Job     project + phase + account                    HourlyJobPhaseAndAccountCode
 *   Non-Job Time     no project, the configured account only      AccountCodeOnly
 *   Service Order    SO (+ its project) + account                 ServiceOrder
 *
 * The tenant opts in with `payrollTimesheetEditing.clockHourlyV2Config` (the
 * same block as MobileApp companyInfo.clockHourlyV2Config). When it is ABSENT
 * (WCDP, NPM, Wallcraft) every row classifies as 'legacy' and the editor keeps
 * today's free-form Hourly modal — nothing here changes their behavior.
 */

export type HourlyMode = 'job' | 'nonJob' | 'serviceOrder';
export type HourlyRowShape = 'legacy' | 'driveTime' | 'pieceHours' | HourlyMode;

export type ClockHourlyV2Config = NonNullable<
  NonNullable<SiteConfig['payrollTimesheetEditing']>['clockHourlyV2Config']
>;

export interface HourlyModeConfig {
  /** False ⇒ the tenant has no clockHourlyV2Config block: legacy editor everywhere. */
  present: boolean;
  /** 'HourlyJob' enabled ⇒ the editor offers the Piece hours kind. */
  pieceHours: boolean;
  /** The Hourly modal's modes, in switch order (job, nonJob, serviceOrder). */
  modes: HourlyMode[];
  /** Non-Job Time's fixed account code; null when that mode is off. */
  nonJobAccountCodeID: number | null;
  rule: ClockHourlyV2Config['hourlyVsPieceRule'] | null;
}

const LEGACY: HourlyModeConfig = {
  present: false, pieceHours: false, modes: [], nonJobAccountCodeID: null, rule: null,
};

/**
 * Resolve the tenant block into the editor's view of it. Only HourlyJob,
 * HourlyJobPhaseAndAccountCode, AccountCodeOnly and ServiceOrder drive behavior;
 * the other PunchPayloadKinds are accepted and ignored for now.
 */
export function resolveHourlyModeConfig(
  cfg: ClockHourlyV2Config | null | undefined = config.payrollTimesheetEditing?.clockHourlyV2Config
): HourlyModeConfig {
  if (!cfg) return LEGACY;
  const kinds = new Set<PayrollPunchPayloadKind>(cfg.enabledPayloadKinds ?? []);
  const section = cfg.hourlyOtherOneAccountCodeSection;
  const nonJobOn = kinds.has('AccountCodeOnly')
    && section?.enabled === true
    && (section.accountCodeID ?? 0) > 0;
  const modes: HourlyMode[] = [];
  if (kinds.has('HourlyJobPhaseAndAccountCode')) modes.push('job');
  if (nonJobOn) modes.push('nonJob');
  if (kinds.has('ServiceOrder')) modes.push('serviceOrder');
  return {
    present: true,
    pieceHours: kinds.has('HourlyJob'),
    modes,
    nonJobAccountCodeID: nonJobOn ? section!.accountCodeID : null,
    rule: cfg.hourlyVsPieceRule,
  };
}

/** Which shape one TEH row is, in the fixed precedence order (legacy first). */
export function classifyHourlyRow(
  row: TimeEntryHourly,
  group: HourlyProject,
  cfg: HourlyModeConfig
): HourlyRowShape {
  if (!cfg.present) return 'legacy';
  if (row.driveTimeEntryID != null) return 'driveTime';
  if (row.serviceOrderID != null || row.serviceOrder != null) return 'serviceOrder';
  const project = group.project?.projectID != null;
  const account = group.account?.accountCodeID != null;
  if (project && !account && cfg.rule === 'hasAccountCode') return 'pieceHours';
  if (project && account) return 'job';
  if (!project && account) return 'nonJob';
  return 'job';
}
